import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePostOrdersUploadParams, TypePostOrdersUploadResponse } from '~/types/delivery-agency.types';

const usePostOrdersUpload = () => {
  const { runAsync } = useAsyncHandler();

  const postOrdersUpload = useAPI<TypePostOrdersUploadParams, TypePostOrdersUploadResponse>({
    endpoint: API_ENDPOINT.DELIVERY_AGENCY.POST_ORDERS_UPLOAD,
    method: 'post',
    axiosConfig: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  const requestOrdersUpload = async (
    params: TypePostOrdersUploadParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypePostOrdersUploadResponse['data']) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => postOrdersUpload({ params }), {
      onSuccess: ({ data }) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestOrdersUpload };
};

const INSUFFICIENT_RESPONSE: TypePostOrdersUploadResponse['data'] = {
  totalCount: 0,
  successCount: 0,
  failCount: 0,
  failDetails: [],
  insufficientAddresses: [
    { rowNums: [12], address: '서울특별시 강남구 테헤란로 231' },
    { rowNums: [13], address: '서울특별시 송파구 올림픽로 300 래미안파크리버 104' },
    { rowNums: [14, 18], address: '경기도 성남시 분당구 판교역로 235' },
    { rowNums: [15], address: '서울특별시 서초구 반포대로 275 반포자이' },
    { rowNums: [16], address: '서울특별시 강남구 테헤란로 152 강남파이낸스센터' },
    { rowNums: [17], address: '서울특별시 마포구 월드컵북로 396' },
  ],
};

registerMockAPI<TypePostOrdersUploadResponse>({
  method: 'post',
  endpoint: '/api-delivery-agency/orders/upload',
  label: '배송대행/주문 엑셀 일괄 등록',
  scenarios: [
    {
      name: '정상 응답 — 전건 성공',
      response: { data: { totalCount: 3, successCount: 3, failCount: 0, failDetails: [] } },
    },
    {
      name: '상세주소 불충분 → [이대로 진행하기] 시 전건 성공',
      responses: [
        { response: { data: INSUFFICIENT_RESPONSE } },
        { response: { data: { totalCount: 7, successCount: 7, failCount: 0, failDetails: [] } } },
      ],
    },
    {
      name: '상세주소 불충분 → [이대로 진행하기] 시 일부 실패',
      responses: [
        { response: { data: INSUFFICIENT_RESPONSE } },
        {
          response: {
            data: {
              totalCount: 7,
              successCount: 6,
              failCount: 1,
              failDetails: [{ rowNums: [16], message: '당일배송 서비스 지역이 아닙니다. 확인 후 재등록해주세요.' }],
            },
          },
        },
      ],
    },
    {
      name: '전건 실패 — 당일배송 조건 미충족',
      response: {
        data: {
          totalCount: 3,
          successCount: 0,
          failCount: 3,
          failDetails: [
            {
              rowNums: [2],
              message:
                '현재 당일배송 서비스 이용 권한이 없습니다. 당일배송 서비스 이용을 원하시는 경우 영업 담당자에게 문의해 주세요.',
            },
            {
              rowNums: [3, 4],
              message: '당일배송 서비스는 낮 12시(정오) 이전 주문만 이용 가능합니다. 확인 후 재등록해주세요.',
            },
            { rowNums: [5], message: '당일배송 서비스 지역이 아닙니다. 확인 후 재등록해주세요.' },
          ],
        },
      },
    },
    {
      name: '일부 실패 — 긴 사유·링크 포함 (줄바꿈 확인용)',
      response: {
        data: {
          totalCount: 5,
          successCount: 3,
          failCount: 2,
          failDetails: [
            {
              rowNums: [2],
              message:
                '현재 당일배송 서비스 이용 권한이 없습니다. 당일배송 서비스 이용을 원하시는 경우 영업 담당자에게 문의해 주세요.',
            },
            {
              rowNums: [4],
              message:
                '엑셀 양식이 올바르지 않습니다. 최신 양식을 내려받아 다시 등록해 주세요. 양식 확인: https://kurly0521.atlassian.net/wiki/spaces/DPD/pages/4828627277 문의는 영업 담당자에게 부탁드립니다.',
            },
          ],
        },
      },
    },
  ],
});

export default usePostOrdersUpload;
